










































import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch, Prop } from 'vue-property-decorator';

import Modal from '.';
import Resizer from './Resizer.vue';
import { inRange, createModalEvent, getMutationObserver } from './util';
import { parseNumber, validateNumber } from './parser';

@Component({
    components: { Resizer },
})
export default class VueJsModal extends Vue {
    private visible = false;
    private $modal: any;

    private visibility = {
        modal: false,
        overlay: false,
    };

    private shift = {
        left: 0,
        top: 0,
    };

    private modal = {
        width: 0,
        widthType: 'px',
        height: 0,
        heightType: 'px',
        renderedHeight: 0,
    };

    private window = {
        width: 0,
        height: 0,
    };

    private mutationObserver = null;

    @Prop({ type: [String], required: true })
    private name: string;

    @Prop({ type: [Number], default: 0 })
    private delay: number;

    @Prop({ type: [Boolean], default: false })
    private resizable: boolean;

    @Prop({ type: [Boolean], default: false })
    private adaptive: boolean;

    @Prop({ type: [Boolean, String], default: false })
    private draggable: boolean | string;

    @Prop({ type: [Boolean], default: false })
    private scrollable: boolean;

    @Prop({ type: [Boolean], default: false })
    private reset: boolean;

    @Prop({ type: [String], default: 'overlay-fade' })
    private overlayTransition: string;

    @Prop({ type: [String] })
    private transition: string;

    @Prop({ type: [Boolean], default: true })
    private clickToClose: boolean;

    @Prop({ type: [String, Array], default: 'v--modal' })
    private classes: string | Array<any>;

    @Prop({
        type: [Number],
        default: 0,
        validator(value) {
            return value >= 0;
        },
    })
    private minWidth: number;

    @Prop({
        type: [Number],
        default: 0,
        validator(value) {
            return value >= 0;
        },
    })
    private minHeight: number;

    @Prop({ type: [Number], default: Infinity })
    private maxWidth: number;

    @Prop({ type: [Number], default: Infinity })
    private maxHeight: number;

    @Prop({ type: [Number, String], default: 600, validator: validateNumber })
    private width: number | string;

    @Prop({
        type: [Number, String],
        default: 300,
        validator(value) {
            return value === 'auto' || validateNumber(value);
        },
    })
    private height: number | string;

    @Prop({
        type: [Number],
        default: 0.5,
        validator(value) {
            return value >= 0 && value <= 1;
        },
    })
    private pivotX: number;

    @Prop({
        type: [Number],
        default: 0.5,
        validator(value) {
            return value >= 0 && value <= 1;
        },
    })
    private pivotY: number;

    private created() {
        if (this.$isServer) return;

        this.setInitialSize();
    }

    /**
     * Sets global listeners
     */
    private beforeMount() {
        Modal.event.$on('toggle', this.handleToggleEvent);

        window.addEventListener('resize', this.handleWindowResize);
        this.handleWindowResize();
        /**
         * Making sure that autoHeight is enabled when using "scrollable"
         */
        if (this.scrollable && !this.isAutoHeight) {
            console.warn(
                `Modal "${this.name}" has scrollable flag set to true ` + `but height is not "auto" (${this.height})`
            );
        }
        /**
         * Only observe when using height: 'auto'
         * The callback will be called when modal DOM changes,
         * this is for updating the `top` attribute for height 'auto' modals.
         */
        if (this.isAutoHeight) {
            /**
             * MutationObserver feature detection:
             *
             * Detects if MutationObserver is available, return false if not.
             * No polyfill is provided here, so height 'auto' recalculation will
             * simply stay at its initial height (won't crash).
             * (Provide polyfill to support IE < 11)
             *
             * https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
             *
             * For the sake of SSR, MutationObserver cannot be initialized
             * before component creation >_<
             */
            const MutationObserver = getMutationObserver();

            if (MutationObserver) {
                this.mutationObserver = new MutationObserver(mutations => {
                    this.updateRenderedHeight();
                });
            }
        }

        if (this.clickToClose) {
            window.addEventListener('keyup', this.handleEscapeKeyUp);
        }
    }
    /**
     * Removes global listeners
     */
    private beforeDestroy() {
        Modal.event.$off('toggle', this.handleToggleEvent);
        window.removeEventListener('resize', this.handleWindowResize);

        if (this.clickToClose) {
            window.removeEventListener('keyup', this.handleEscapeKeyUp);
        }
        /**
         * Removes blocked scroll
         */
        if (this.scrollable) {
            document.body.classList.remove('v--modal-block-scroll');
        }
    }

    /**
     * Returns true if height is set to "auto"
     */
    get isAutoHeight() {
        return this.modal.heightType === 'auto';
    }
    /**
     * Calculates and returns modal position based on the
     * pivots, window size and modal size
     */
    get position() {
        const { window, shift, pivotX, pivotY, trueModalWidth, trueModalHeight } = this;

        const maxLeft = window.width - trueModalWidth;
        const maxTop = window.height - trueModalHeight;

        const left = shift.left + pivotX * maxLeft;
        const top = shift.top + pivotY * maxTop;

        return {
            left: parseInt(inRange(0, maxLeft, left)),
            top: parseInt(inRange(0, maxTop, top)),
        };
    }
    /**
     * Returns pixel width (if set with %) and makes sure that modal size
     * fits the window
     */
    get trueModalWidth() {
        const { window, modal, adaptive, minWidth, maxWidth } = this;

        const value = modal.widthType === '%' ? (window.width / 100) * modal.width : modal.width;

        const max = Math.min(window.width, maxWidth);

        return adaptive ? inRange(minWidth, max, value) : value;
    }
    /**
     * Returns pixel height (if set with %) and makes sure that modal size
     * fits the window.
     *
     * Returns modal.renderedHeight if height set as "auto"
     */
    get trueModalHeight() {
        const { window, modal, isAutoHeight, adaptive, maxHeight } = this;

        const value = modal.heightType === '%' ? (window.height / 100) * modal.height : modal.height;

        if (isAutoHeight) {
            // use renderedHeight when height 'auto'
            return this.modal.renderedHeight;
        }

        const max = Math.min(window.height, maxHeight);

        return adaptive ? inRange(this.minHeight, max, value) : value;
    }
    /**
     * Returns class list for screen overlay (modal background)
     */
    get overlayClass() {
        return {
            'v--modal-overlay': true,
            scrollable: this.scrollable && this.isAutoHeight,
        };
    }
    /**
     * Returns class list for modal itself
     */
    get modalClass() {
        return ['v--modal-box', this.classes];
    }
    /**
     * CSS styles for position and size of the modal
     */
    get modalStyle() {
        return {
            top: this.position.top + 'px',
            left: this.position.left + 'px',
            width: this.trueModalWidth + 'px',
            height: this.isAutoHeight ? 'auto' : this.trueModalHeight + 'px',
        };
    }

    private handleToggleEvent(name, state, params) {
        if (this.name === name) {
            const nextState = typeof state === 'undefined' ? !this.visible : state;

            return this.toggle(nextState, params);
        }

        if (!state) {
            const nextState = typeof state === 'undefined' ? !this.visible : state;

            return this.toggle(nextState, params);
        }
    }
    /**
     * Initializes modal's size & position,
     * if "reset" flag is set to true - this function will be called
     * every time "beforeOpen" is triggered
     */
    private setInitialSize() {
        const { modal } = this;
        const width = parseNumber(this.width);
        const height = parseNumber(this.height);

        modal.width = width.value;
        modal.widthType = width.type;
        modal.height = height.value;
        modal.heightType = height.type;
    }

    private handleEscapeKeyUp(event) {
        if (event.which === 27 && this.visible) {
            this.$modal.hide(this.name);
        }
    }

    private handleWindowResize() {
        this.window.width = window.innerWidth;
        this.window.height = window.innerHeight;
    }
    /**
     * Generates event object
     */
    private createModalEvent(args = {}) {
        return createModalEvent({
            name: this.name,
            ref: this.$refs.modal,
            ...args,
        });
    }

    /**
     * Event handler which is triggered on modal resize
     */
    private handleModalResize(event) {
        this.modal.widthType = 'px';
        this.modal.width = event.size.width;

        this.modal.heightType = 'px';
        this.modal.height = event.size.height;

        const { size } = this.modal as any;

        this.$emit('resize', this.createModalEvent({ size }));
    }
    /**
     * Event handler which is triggered on $modal.show and $modal.hide
     * BeforeEvents: ('before-close' and 'before-open') are `$emit`ed here,
     * but AfterEvents ('opened' and 'closed') are moved to `watch.visible`.
     */
    private toggle(nextState, params?) {
        const { reset, scrollable, visible } = this;

        if (visible === nextState) {
            return;
        }

        const beforeEventName = visible ? 'before-close' : 'before-open';

        if (beforeEventName === 'before-open') {
            /**
             * Need to unfocus previously focused element, otherwise
             * all keypress events (ESC press, for example) will trigger on that element.
             */
            if (
                document.activeElement &&
                document.activeElement.tagName !== 'BODY' &&
                (document.activeElement as any).blur
            ) {
                (document.activeElement as any).blur();
            }

            if (reset) {
                this.setInitialSize();

                this.shift.left = 0;
                this.shift.top = 0;
            }

            if (scrollable) {
                document.body.classList.add('v--modal-block-scroll');
            }
        } else {
            if (scrollable) {
                document.body.classList.remove('v--modal-block-scroll');
            }
        }

        let stopEventExecution = false;

        const stop = () => {
            stopEventExecution = true;
        };

        const beforeEvent = this.createModalEvent({
            stop,
            state: nextState,
            params,
        });

        this.$emit(beforeEventName, beforeEvent);

        if (!stopEventExecution) {
            this.visible = nextState;
            this.visible ? this.startOpeningModal() : this.startClosingModal();
        }
    }

    private getDraggableElement() {
        const selector = typeof this.draggable !== 'string' ? '.v--modal-box' : this.draggable;

        return selector ? (this.$refs.overlay as any).querySelector(selector) : null;
    }
    /**
     * Event handler that is triggered when background overlay is clicked
     */
    private handleBackgroundClick() {
        if (this.clickToClose) {
            this.toggle(false);
        }
    }

    private startOpeningModal() {
        this.visibility.overlay = true;

        setTimeout(() => {
            this.visibility.modal = true;
        }, this.delay);
    }

    private startClosingModal() {
        this.visibility.modal = false;

        setTimeout(() => {
            this.visibility.overlay = false;
        }, this.delay);
    }

    private addDraggableListeners() {
        if (!this.draggable) {
            return;
        }

        const dragger = this.getDraggableElement();

        if (dragger) {
            let startX = 0;
            let startY = 0;
            let cachedShiftX = 0;
            let cachedShiftY = 0;

            const getPosition = event => {
                return event.touches && event.touches.length > 0 ? event.touches[0] : event;
            };

            const handleDraggableMousedown = event => {
                const target = event.target;

                if (target && target.nodeName === 'INPUT') {
                    return;
                }

                const { clientX, clientY } = getPosition(event);

                /* eslint-disable @typescript-eslint/no-use-before-define */
                document.addEventListener('mousemove', handleDraggableMousemove);
                document.addEventListener('touchmove', handleDraggableMousemove);

                document.addEventListener('mouseup', handleDraggableMouseup);
                document.addEventListener('touchend', handleDraggableMouseup);

                startX = clientX;
                startY = clientY;

                cachedShiftX = this.shift.left;
                cachedShiftY = this.shift.top;
            };

            const handleDraggableMousemove = event => {
                const { clientX, clientY } = getPosition(event);

                this.shift.left = cachedShiftX + clientX - startX;
                this.shift.top = cachedShiftY + clientY - startY;

                event.preventDefault();
            };

            const handleDraggableMouseup = event => {
                document.removeEventListener('mousemove', handleDraggableMousemove);
                document.removeEventListener('touchmove', handleDraggableMousemove);

                document.removeEventListener('mouseup', handleDraggableMouseup);
                document.removeEventListener('touchend', handleDraggableMouseup);

                event.preventDefault();
            };

            dragger.addEventListener('mousedown', handleDraggableMousedown);
            dragger.addEventListener('touchstart', handleDraggableMousedown);
        }
    }

    private removeDraggableListeners() {
        /**
         * Ideally this is not needed because "dragger" will be unmounted anyway.
         */
    }
    /**
     * Update $data.modal.renderedHeight using getBoundingClientRect.
     * This method is called when:
     * 1. modal opened
     * 2. MutationObserver's observe callback
     */
    private updateRenderedHeight() {
        if (this.$refs.modal) {
            this.modal.renderedHeight = (this.$refs.modal as any).getBoundingClientRect().height;
        }
    }
    /**
     * Start observing modal's DOM, if childList or subtree changes,
     * the callback (registered in beforeMount) will be called.
     */
    private connectObserver() {
        if (this.mutationObserver) {
            this.mutationObserver.observe(this.$refs.overlay, {
                childList: true,
                attributes: true,
                subtree: true,
            });
        }
    }

    /**
     * Disconnects MutationObserver
     */
    private disconnectObserver() {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }

    private beforeTransitionEnter() {
        this.connectObserver();
    }

    private afterTransitionEnter() {
        this.addDraggableListeners();

        this.$emit('opened', this.createModalEvent({ state: true }));
    }

    private afterTransitionLeave() {
        this.removeDraggableListeners();
        this.disconnectObserver();
        this.$emit('closed', this.createModalEvent({ state: false }));
    }
}
